// Microphone capture for the gateway's audioInput path: 16 kHz mono signed 16-bit frames,
// the format Transcribe's stream is opened with server-side.

/** Samples per outbound frame. 1024 at 16 kHz is 64 ms, well inside AWS's 100 ms guidance. */
const FRAME_SAMPLES = 1024;

export const MIC_SAMPLE_RATE = 16000;
export const MIC_BITS_PER_SAMPLE = 16;
export const MIC_CHANNELS = 1;
export const MIC_ENCODING = 'pcm';

/** A running capture. Frames arrive on the callback until {@link MicStream.stop} is called. */
export interface MicStream {
  /** The device the browser opened, for a diagnostic that has to name it. */
  readonly deviceLabel: string;
  stop(): void;
}

// Accumulates the 128-sample quanta the audio thread renders in into whole frames, so the socket
// sees one message per 64 ms instead of one per 8 ms.
const WORKLET_SOURCE = `
class PcmFrameProcessor extends AudioWorkletProcessor {
  constructor(options) {
    super();
    this.frameSamples = options.processorOptions.frameSamples;
    this.buffer = new Int16Array(this.frameSamples);
    this.filled = 0;
  }

  process(inputs) {
    const channel = inputs[0] && inputs[0][0];
    if (!channel) return true;

    for (let i = 0; i < channel.length; i++) {
      const sample = Math.max(-1, Math.min(1, channel[i]));
      // Asymmetric scale: 32767 for positive, 32768 for negative, so full-scale
      // negative samples do not wrap to positive.
      this.buffer[this.filled++] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;

      if (this.filled === this.frameSamples) {
        const frame = this.buffer.slice();
        this.port.postMessage(frame.buffer, [frame.buffer]);
        this.filled = 0;
      }
    }
    return true;
  }
}

registerProcessor('pcm-frames', PcmFrameProcessor);
`;

const AUDIO_CONSTRAINTS = {
  // Not optional. Playback continues while the microphone is open, and without cancellation
  // the agent hears itself and transcribes its own voice.
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  channelCount: MIC_CHANNELS,
} satisfies MediaTrackConstraints;

/**
 * Opens the system default input, by name.
 *
 * Asking for no device at all is not the same thing: Chrome then hands the page whatever input it
 * has pinned to this origin, and that pin outlives the device it was made for. A pin left on a
 * built-in microphone can deliver a stream of pure silence while the device the user is speaking
 * into sits one entry away in the list — an utterance of zeros the gateway can only answer with
 * nothing. Only `exact` overrides the pin; `ideal` is a preference Chrome considers already
 * satisfied. The synthetic `default` id is Chrome's alone, so a browser without it — every other
 * one, where there is no per-origin pin to override either — falls back to its own default.
 */
async function openDefaultInput(): Promise<MediaStream> {
  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: { deviceId: { exact: 'default' }, ...AUDIO_CONSTRAINTS },
    });
  } catch (error) {
    if (!(error instanceof DOMException) || error.name !== 'OverconstrainedError') throw error;
    return navigator.mediaDevices.getUserMedia({ audio: AUDIO_CONSTRAINTS });
  }
}

/**
 * Opens the microphone and delivers PCM frames.
 *
 * @param onFrame receives one frame of signed 16-bit little-endian mono samples at
 *     {@link MIC_SAMPLE_RATE}
 * @returns the running capture, to be stopped when the utterance ends
 */
export async function startMicCapture(onFrame: (frame: ArrayBuffer) => void): Promise<MicStream> {
  const stream = await openDefaultInput();

  const context = new AudioContext({ sampleRate: MIC_SAMPLE_RATE });

  // A Blob rather than a separate worklet file: the processor is a dozen lines and this keeps it
  // from becoming a build-configuration question about how the asset is emitted.
  const workletUrl = URL.createObjectURL(new Blob([WORKLET_SOURCE], { type: 'text/javascript' }));
  try {
    await context.audioWorklet.addModule(workletUrl);
  } finally {
    URL.revokeObjectURL(workletUrl);
  }

  const source = context.createMediaStreamSource(stream);
  const worklet = new AudioWorkletNode(context, 'pcm-frames', {
    numberOfOutputs: 1,
    processorOptions: { frameSamples: FRAME_SAMPLES },
  });

  worklet.port.onmessage = (event: MessageEvent<ArrayBuffer>) => onFrame(event.data);

  // A worklet is only pulled if it reaches the destination, but routing the microphone to the
  // speakers would be a feedback loop — so it reaches it through a muted gain node.
  const mute = context.createGain();
  mute.gain.value = 0;

  source.connect(worklet);
  worklet.connect(mute);
  mute.connect(context.destination);

  return {
    deviceLabel: stream.getAudioTracks()[0]?.label ?? 'unknown device',
    stop() {
      worklet.port.onmessage = null;
      source.disconnect();
      worklet.disconnect();
      mute.disconnect();
      stream.getTracks().forEach((track) => track.stop());
      void context.close();
    },
  };
}

/** Base64-encodes a PCM frame for the audioInput message's base64Audio field. */
export function encodeFrame(frame: ArrayBuffer): string {
  const bytes = new Uint8Array(frame);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

/** Rough loudness of a frame, 0–1, for the level meter on the talk button. */
export function frameLevel(frame: ArrayBuffer): number {
  const samples = new Int16Array(frame);
  let peak = 0;
  for (let i = 0; i < samples.length; i++) {
    const magnitude = Math.abs(samples[i]);
    if (magnitude > peak) peak = magnitude;
  }
  return peak / 32768;
}
